// iter 144g - Full partner profile page (member portal).
//
// Replaces the old behaviour where clicking a partner card jumped straight
// to the contact form. New route: `/portal/partners/:partnerId` shows the
// complete profile (logo, name, industry, cities, bio, thumbnail, gallery
// image, languages, years of experience, website) and a single
// "Contact via Mundalo" CTA at the bottom that routes to the existing
// contact form at `/portal/partners/:partnerId/contact`.
//
// `contact_email` and `phone` are stripped server-side via _PUBLIC_PROJECTION
// - they never reach this page.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ExternalLink, MapPin, Languages, Award, Briefcase, Globe } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SectionLabel = ({ children }) => (
  <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E] mb-2">
    {children}
  </p>
);

const PortalPartnerProfile = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const ret = `/portal/partners/${partnerId}`;
      navigate(`/start?return=${encodeURIComponent(ret)}`, { replace: true });
    }
  }, [authLoading, user, partnerId, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/partners/${partnerId}`);
        if (!r.ok) {
          if (!cancelled) setErr('not_found');
          return;
        }
        const data = await r.json();
        if (!cancelled) setPartner(data);
      } catch (_) {
        if (!cancelled) setErr('network');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  if (loading) {
    return (
      <PortalLayout>
        <div className="min-h-[60vh] flex items-center justify-center text-[#5A6B73]">
          Loading partner profile…
        </div>
      </PortalLayout>
    );
  }

  if (err || !partner) {
    return (
      <PortalLayout>
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-3">
            Partner not found
          </h2>
          <p className="text-[#5A6B73] mb-6">
            This partner may have been removed or the link is incorrect.
          </p>
          <Link
            to="/portal/partners"
            className="inline-flex items-center gap-2 text-[#2CA8A0] font-mono uppercase text-xs tracking-wider"
          >
            <ArrowLeft className="w-4 h-4" /> Back to partner directory
          </Link>
        </div>
      </PortalLayout>
    );
  }

  const isVetted = partner.partner_type === 'vetted_professional';
  const cities = (partner.cities || []).filter(Boolean);
  const languages = (partner.languages || []).filter(Boolean);

  return (
    <PortalLayout>
      <Helmet>
        <title>{partner.name} | Mundalo Partners</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        {/* Back link */}
        <Link
          to="/portal/partners"
          data-testid="back-to-directory"
          className="inline-flex items-center gap-2 text-[#5A6B73] hover:text-[#2CA8A0] text-xs font-mono uppercase tracking-wider mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to partner directory
        </Link>

        {/* Logo + Name + Industry */}
        <header className="flex flex-col md:flex-row md:items-start gap-6 mb-10">
          {partner.logo_url ? (
            <img
              src={partner.logo_url}
              alt={`${partner.name} logo`}
              data-testid="profile-logo"
              className="w-28 h-28 md:w-32 md:h-32 object-contain bg-white rounded-2xl border border-[#0A1F2E]/8 p-3"
            />
          ) : (
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-2xl bg-[#F4ECD8] border border-[#0A1F2E]/8 flex items-center justify-center text-[#1A5C6E] font-display italic text-3xl">
              {partner.name?.charAt(0) || '?'}
            </div>
          )}

          <div className="flex-1">
            {isVetted && (
              <span
                data-testid="vetted-badge"
                className="inline-block mb-2 font-mono uppercase text-[10px] tracking-[0.18em] px-2.5 py-1 rounded bg-[#2CA8A0]/10 text-[#1A5C6E]"
              >
                Vetted Professional
              </span>
            )}
            <h1
              data-testid="profile-name"
              className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] leading-tight mb-1"
            >
              {partner.name}
            </h1>
            <p className="text-[#5A6B73] text-base flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> {partner.category}
            </p>
            {cities.length > 0 && (
              <p className="mt-2 text-sm text-[#5A6B73] flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{cities.join(' · ')}</span>
              </p>
            )}
          </div>
        </header>

        {/* Bio */}
        {partner.description && (
          <section className="mb-10" data-testid="profile-bio">
            <SectionLabel>About</SectionLabel>
            <p className="text-[#0A1F2E] text-[16px] leading-relaxed whitespace-pre-line">
              {partner.description}
            </p>
          </section>
        )}

        {/* Thumbnail + Gallery image side-by-side on desktop, stacked on mobile */}
        {(partner.photo_url || partner.gallery_url) && (
          <section className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {partner.photo_url && (
              <img
                src={partner.photo_url}
                alt={`${partner.name} portrait`}
                data-testid="profile-thumbnail"
                className="w-full rounded-2xl object-cover aspect-square bg-[#F4ECD8]"
              />
            )}
            {partner.gallery_url && (
              <img
                src={partner.gallery_url}
                alt={`${partner.name} gallery`}
                data-testid="profile-gallery"
                className="w-full rounded-2xl object-cover aspect-square bg-[#F4ECD8]"
              />
            )}
          </section>
        )}

        {/* Vetted-professional detail grid */}
        {isVetted && (
          <section className="mb-10 bg-white rounded-2xl border border-[#0A1F2E]/8 p-6 md:p-7 space-y-5">
            <SectionLabel>Practice details</SectionLabel>

            {languages.length > 0 && (
              <div data-testid="profile-languages">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                  Languages spoken
                </p>
                <p className="text-[#0A1F2E] flex items-center gap-2">
                  <Languages className="w-4 h-4 text-[#1A5C6E]" />
                  {languages.join(', ')}
                </p>
              </div>
            )}

            {partner.expat_experience && (
              <div data-testid="profile-experience">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                  Experience with American / expat clients
                </p>
                <p className="text-[#0A1F2E] flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#1A5C6E]" />
                  {partner.expat_experience}
                </p>
              </div>
            )}

            {partner.credentials && (
              <div data-testid="profile-credentials">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                  Credentials
                </p>
                <p className="text-[#0A1F2E] text-sm whitespace-pre-line">{partner.credentials}</p>
              </div>
            )}

            {partner.fee_structure && (
              <div data-testid="profile-fees">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                  Fee structure
                </p>
                <p className="text-[#0A1F2E] text-sm whitespace-pre-line">{partner.fee_structure}</p>
              </div>
            )}

            {partner.website_url && (
              <div data-testid="profile-website">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                  Website
                </p>
                <a
                  href={partner.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2CA8A0] inline-flex items-center gap-1.5 break-all"
                >
                  <Globe className="w-4 h-4" />
                  {partner.website_url}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            )}
          </section>
        )}

        {/* CTA - only vetted_professionals can be contacted through Mundalo's
            managed messaging. Institutional affiliates link out via their
            website_url. */}
        <div className="bg-[#F4ECD8] rounded-2xl p-7 md:p-8 text-center">
          <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E] mb-3">
            Ready to connect?
          </p>
          <h3 className="font-display italic text-2xl md:text-3xl text-[#0A1F2E] mb-5">
            {isVetted
              ? `Send ${partner.name?.split(' ')[0] || partner.name} a message through Mundalo.`
              : `Visit ${partner.name} directly.`}
          </h3>

          {isVetted ? (
            <Link
              to={`/portal/partners/${partner.id}/contact`}
              data-testid="profile-contact-cta"
              className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-sm"
            >
              Contact via Mundalo
            </Link>
          ) : partner.website_url ? (
            <a
              href={partner.website_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="profile-external-cta"
              className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-sm"
            >
              Visit website <ExternalLink className="w-4 h-4" />
            </a>
          ) : null}

          {isVetted && (
            <p className="mt-4 text-xs text-[#5A6B73]">
              Your message is brokered through Mundalo. {partner.name} replies via a secure link - your contact details stay private until you choose to share them.
            </p>
          )}
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalPartnerProfile;
